import React, { useRef, useEffect, lazy } from 'react';

import { Quill } from 'react-quill';

import { usePendoEventTracker, PendoEvent } from 'common/hooks/usePendoEventTracker';
import { EmojiBlot } from 'common/response-editor/blots/EmojiBlot';
import { HashTagBlot } from 'common/response-editor/blots/HashTagBlot';
import { MentionBlot } from 'common/response-editor/blots/MentionBlot';
import { TemplateUrlBlot } from 'common/response-editor/blots/TemplateUrlBlot';
import { VariableBlot } from 'common/response-editor/blots/VariableBlot';

import 'react-quill/dist/quill.snow.css';

const ReactQuill = lazy(() => import('react-quill'));

// sanitize Link urls
const Link = Quill.import('formats/link');

Link.sanitize = (url: string): string => {
    let newUrl = url;

    if (!newUrl.match(/^http/)) {
        newUrl = `http://${url}`;
    }
    return newUrl;
};

Quill.register({ 'formats/variable': VariableBlot }, true);
Quill.register({ 'formats/mention': MentionBlot }, true);
Quill.register({ 'formats/emoji': EmojiBlot }, true);
Quill.register({ 'formats/template-url': TemplateUrlBlot }, true);
Quill.register({ 'formats/hashtag': HashTagBlot }, true);

type ReactQuillProps = React.ComponentPropsWithRef<typeof ReactQuill>;

interface TrackedReactQuillProps extends ReactQuillProps {
    eventsToTrack: PendoEvent[];
    forwardedRef: (instance: any) => void;
}

const TrackedReactQuill: React.FC<TrackedReactQuillProps> = ({ eventsToTrack, forwardedRef, id, ...rest }) => {
    const quillRef = useRef<any>(null);
    const inputRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        if (quillRef.current) {
            forwardedRef(quillRef.current);
            inputRef.current = quillRef.current.getEditor().root;
        }
    }, [forwardedRef]);

    usePendoEventTracker(inputRef, {
        eventsToTrack,
    });

    return <ReactQuill ref={quillRef} {...rest} />;
};

export default TrackedReactQuill;
